import React, {useContext, useEffect, useRef, useState} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {useForm} from "react-hook-form";

import AuthContext from "../../store/auth-context";
import gs from "../../services/global";
import axios from "axios";

import Header from "../../components/Header";
import InnerBgComp from "../../components/InnerBgComp";
import {FlowSteps, hasInstanceId, OBV_SEARCH_ID_PARAM, stepFun, toasterConfig} from "../../utils/Constant";
import { motion } from "framer-motion";
import { toast } from "react-toastify";
import animationData from "../../assests/json/loader_1.json";
import {Player} from "@lottiefiles/react-lottie-player";

const {
    REACT_APP_DEVELOPMENTURL,
    REACT_APP_FLOWID,
} = process.env;

const Bank = () => {
    const authCtx = useContext(AuthContext);
    const instanceId = authCtx?.instanceId;

    const navigate = useNavigate();

    const [searchParams] = useSearchParams();
    const [isCallback] = useState(() => searchParams.get('callback') === '1');
    const [disableBtn, setDisableBtn] = useState(false);
    const [termCheckbox, setTermCheckbox] = useState(false);
    const [isFailed] = useState(() => searchParams.get('fail') === '1');
    const [isAborted] = useState(() => searchParams.get('abort') === '1');
    const [searchId] = useState(() => searchParams.get(OBV_SEARCH_ID_PARAM));
    // With a search id the instance is resolved from it, so a return in another browser works too.
    const [returnInstanceId, setReturnInstanceId] = useState(
        () => !searchId && hasInstanceId(instanceId) ? instanceId : null
    );

    const loaderAnim = useRef(null);
    const animPlay = () => {
        loaderAnim.current.play();
    };

    useEffect(() => {
        if (!searchId) return;

        axios
            .get(`/flow/${REACT_APP_FLOWID}/instances/external-id/${encodeURIComponent(searchId)}`)
            .then((response) => {
                const resolvedInstanceId = response?.data?.instanceId;
                const currentStep = response?.data?.currentStepId;

                authCtx.createInstanceFunc(resolvedInstanceId);

                if (currentStep !== FlowSteps.BANK) {
                    navigate(stepFun(resolvedInstanceId)[currentStep], { replace: true });
                    return;
                }

                setReturnInstanceId(resolvedInstanceId);
            })
            .catch((error) => {
                // Fall back to the instance this browser started, as before the lookup existed.
                if (hasInstanceId(instanceId)) {
                    setReturnInstanceId(instanceId);
                    return;
                }

                if (error?.response?.status === 404) {
                    toast.error("Your bank verification link has expired.", toasterConfig);
                } else {
                    toast.error("Something went wrong.", toasterConfig);
                }
                navigate("/", { replace: true });
            });
    }, [searchId]);

    useEffect(() => {
        if (!isCallback) return;

        // No search id and no instance in this browser: polling would only hit /instances/null.
        if (!searchId && !returnInstanceId) {
            toast.error("Your bank verification link has expired.", toasterConfig);
            navigate("/", { replace: true });
            return;
        }

        if (!returnInstanceId) return;

        const interval = setInterval(async () => {
            try {
                let payload = {
                    action: "check",
                    data: {},
                };

                const result = await axios
                    .post(`/flow/${REACT_APP_FLOWID}/instances/${returnInstanceId}`, payload)

                const currentStep = result?.data?.currentStepId;
                const isCompleted = result?.data?.responseData?.completed;
                const errorMessages = result?.data?.errors;

                if (isCompleted === true || currentStep !== FlowSteps.BANK) {
                    navigate(stepFun()[currentStep], { replace: true });
                }

                if (errorMessages && errorMessages.length > 0) {
                    toast.error("Something went wrong.", toasterConfig);
                }
            } catch (error) {
                toast.error("Something went wrong.", toasterConfig);
            }
        }, 3000);

        return () => clearInterval(interval);
    }, [isCallback, returnInstanceId]);

    useEffect(() => {
        if (isFailed || isAborted) {
            toast.error("Bank verification failed.", toasterConfig);
        }
    }, [isFailed, isAborted]);

    const {
        handleSubmit,
    } = useForm({
        mode: "all",
    });

    const onSubmit = () => {
        let payload = {
            action: "submit",
            data: {},
        };

        setDisableBtn(true);
        gs.showLoader(true);

        axios
            .post(`/flow/${REACT_APP_FLOWID}/instances/${instanceId}`, payload)
            .then((response) => {
                const result = response?.data;
                const redirectUrl = result?.responseData?.obvSearchUrl;

                if (redirectUrl) {
                    window.location.href = redirectUrl;
                } else {
                    toast.error("Something went wrong.", toasterConfig);
                }

                setDisableBtn(false);
                gs.showLoader(false);
            })
            .catch((error) => {
                const errorEnd = error?.response?.data?.currentStepId;
                navigate(stepFun()[errorEnd], { replace: true });
                const errorResult = error?.response?.data?.errors;
                const arrayOfString = Object.values(errorResult);
                const arrayFlatten = arrayOfString.flat();

                setDisableBtn(false);
                gs.showLoader(false);

                for (let itemData of arrayFlatten) {
                    toast.error(itemData, toasterConfig);
                }
            });
    };

    localStorage.setItem("stUrl", REACT_APP_DEVELOPMENTURL);
    localStorage.setItem("flowId", REACT_APP_FLOWID);

    return (
        <div>
            <Header />
            <div className="main-container">
                <InnerBgComp classes={"inner-bg-img"} />

                <div className="container">
                    {!isCallback && (
                        <motion.div
                            initial={{ y: 60, scale: 0.9, opacity: 0 }}
                            animate={{ y: 0, scale: 1, opacity: 1 }}
                            transition={{ duration: 0.5 }}
                            className="comm-box congo-checkout"
                        >
                            <div className="comm-hdn-slide">
                                <div className="comm-hdn-box">
                                    <h3 className="comm-hdn">We need your consent to verify your bank statements</h3>
                                    <div className="comm-para">
                                        <p>You will be redirected to LendingMetrics’ open-banking service (OohMoolah) to verify your bank account details.</p>
                                    </div>
                                </div>
                                <div className="tick-anim">
                                    <i className="icon-check" />
                                </div>
                            </div>
                            <form id="congoForm" onSubmit={handleSubmit(onSubmit)}>
                                <div className="check-box">
                                    <input
                                        type="checkbox"
                                        name="termCheckbox"
                                        id="term"
                                        defaultChecked={termCheckbox}
                                        onClick={(e) => setTermCheckbox(e.target.checked)}
                                    />
                                    <label className="check-label" htmlFor="term">
                                        I agree to be redirected to a third-party partner for the verification of my bank details.
                                    </label>
                                </div>
                                <button className="button button--block" disabled={!termCheckbox || disableBtn}>Continue</button>
                            </form>
                        </motion.div>
                    )}
                    {isCallback && (
                        <motion.div
                            initial={{ scale: 0, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ duration: 0.5 }}
                            className="comm-box congo-box"
                        >
                            <motion.div
                                initial={{ scale: 0, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                transition={{ duration: 0.5 }}
                                className="congo-lottie"
                            >
                                <Player
                                    onEvent={(event) => {
                                        if (event === "load") {
                                            animPlay();
                                        }
                                    }}
                                    ref={loaderAnim}
                                    autoplay={true}
                                    loop={true}
                                    controls={true}
                                    src={animationData}
                                />
                            </motion.div>
                            <div className="congo-cont">
                                <div>
                                    <motion.h4
                                        initial={{ y: 60, opacity: 0 }}
                                        animate={{ y: 0, opacity: 1 }}
                                        transition={{ duration: 0.5 }}
                                        className="congo-hdn"
                                    >
                                        Almost done
                                    </motion.h4>
                                    <motion.div
                                        initial={{ y: 60, opacity: 0 }}
                                        animate={{ y: 0, opacity: 1 }}
                                        transition={{ duration: 0.5, delay: 0.2 }}
                                        className="congo-para"
                                    >
                                        <p>
                                            We are processing your application.
                                        </p>
                                    </motion.div>
                                </div>
                            </div>
                        </motion.div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Bank;